<template>
  <div class="customer-type-selection">
    <div class="header-section">
      <h1>How will you use Structure?</h1>
      <p>Select the product(s) you're subscribing to. You can add more later from your account.</p>
    </div>

    <div class="card-section">
      <v-row justify="center">
        <v-col
          v-for="option in customerTypeOptions"
          :key="option.value"
          cols="12"
          md="4"
        >
          <v-card
            class="mx-auto type-card"
            :class="{ 'selected-card': selectedType === option.value }"
            variant="outlined"
            @click="selectedType = option.value"
          >
            <v-card-text class="text-center pa-6">
              <v-icon size="48" color="primary" class="mb-4">{{ option.icon }}</v-icon>
              <h2 class="text-h6 mb-2">{{ option.title }}</h2>
              <p class="text-body-2 text-medium-emphasis">{{ option.description }}</p>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <div v-if="errorMessage" class="error-section mt-4">
      <v-alert type="error" density="compact">{{ errorMessage }}</v-alert>
    </div>

    <div class="continue-button-section">
      <v-btn
        color="primary"
        large
        class="str-btn-primary"
        :disabled="!selectedType || isLoading"
        @click="handleContinue"
      >
        <v-progress-circular v-if="isLoading" indeterminate size="20" class="mr-2" />
        Continue
      </v-btn>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useAuth0 } from '@auth0/auth0-vue';
import axios from 'axios';

const router = useRouter();
const store = useStore();
const { getAccessTokenSilently, user } = useAuth0();

const selectedType = ref(store.state.authentication.customer_type || null);
const isLoading = ref(false);
const errorMessage = ref(null);

const customerTypeOptions = [
  {
    value: 'sdk',
    title: 'Structure SDK Developer',
    description: 'Build apps with the Structure SDK. Access API keys, downloads, and usage analytics.',
    icon: 'mdi-code-braces',
  },
  {
    value: 'capture',
    title: 'Structure Capture Enterprise',
    description: 'Manage your Structure Capture MDM deployment. Control activation keys and monitor device seats.',
    icon: 'mdi-devices',
  },
  {
    value: 'both',
    title: 'Both',
    description: 'Access the full Structure platform — SDK developer tools and Capture Enterprise management.',
    icon: 'mdi-view-grid-plus',
  },
];

async function handleContinue() {
  if (!selectedType.value) return;

  isLoading.value = true;
  errorMessage.value = null;

  try {
    const token = await getAccessTokenSilently();
    await axios.patch(
      `${process.env.VUE_APP_CBE_BASE_URL}/piccolo/users/${user.value.sub}/customer-type`,
      { customer_type: selectedType.value },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    store.commit('SET_AUTHENTICATION', {
      ...store.state.authentication,
      customer_type: selectedType.value,
    });

    const nextRoute =
      selectedType.value === 'capture'
        ? { name: 'BillingInfo' }
        : { name: 'SubscriptionTierSelection' };

    router.push(nextRoute);
  } catch (err) {
    errorMessage.value =
      err.response?.data?.detail ||
      err.response?.data?.message ||
      err.response?.data?.error ||
      'Failed to save your selection. Please try again.';
  } finally {
    isLoading.value = false;
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/styles/CustomerTypeSelection.scss'
</style>
